import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import url from '../constants/config';
import ConfirmModal from '../shared/displayComponents/confirmModal/ConfirmModal';

const getAuthHeader = () => ({
  Authorization: `Bearer ${window.localStorage.getItem('authorization')}`,
});

const formatBytes = (bytes) => {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('pl-PL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const AdminDocuments = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  /* Filters */
  const [filterPlayer, setFilterPlayer] = useState('');
  const [filterUploadedBy, setFilterUploadedBy] = useState('');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');

  const fetchDocuments = useCallback(() => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams();
    if (filterFrom) params.set('from', new Date(filterFrom).toISOString());
    if (filterTo) params.set('to', new Date(filterTo + 'T23:59:59').toISOString());

    axios
      .get(`${url}/admin/documents?${params.toString()}`, { headers: getAuthHeader() })
      .then(res => setDocuments(res.data.documents || []))
      .catch(err => setError(err.response?.data?.message || 'Nie udało się załadować dokumentów.'))
      .finally(() => setLoading(false));
  }, [filterFrom, filterTo]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  /* Client-side player/uploadedBy filter */
  const filtered = documents.filter(doc => {
    if (filterPlayer && !doc.playerName.toLowerCase().includes(filterPlayer.toLowerCase())) return false;
    if (filterUploadedBy && !doc.uploadedBy?.toLowerCase().includes(filterUploadedBy.toLowerCase())) return false;
    return true;
  });

  const handleDownload = (doc) => {
    const downloadUrl = `${url}/checklist/${doc.playerId}/tasks/${doc.taskId}/attachments/${doc.attachmentId}`;
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', doc.originalName || 'attachment');
    link.setAttribute('target', '_blank');
    // Need auth header — open in new tab (the existing endpoint handles auth via cookie or we just open)
    // Since it's behind requireRole, we need to pass the token. Use axios to fetch and blob-download.
    axios
      .get(downloadUrl, { headers: getAuthHeader(), responseType: 'blob' })
      .then(res => {
        const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = doc.originalName || 'attachment';
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(blobUrl);
      })
      .catch(() => alert('Nie udało się pobrać pliku.'));
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    setDeleting(true);
    const deleteUrl = `${url}/checklist/${deleteTarget.playerId}/tasks/${deleteTarget.taskId}/attachments/${deleteTarget.attachmentId}`;
    axios
      .delete(deleteUrl, { headers: getAuthHeader() })
      .then(() => {
        setDocuments(prev => prev.filter(d => d.attachmentId !== deleteTarget.attachmentId));
        setDeleteTarget(null);
      })
      .catch(() => alert('Nie udało się usunąć pliku.'))
      .finally(() => setDeleting(false));
  };

  return (
    <div style={{ padding: '24px 32px', maxWidth: '1300px' }}>
      <h2 style={{ margin: '0 0 20px', color: '#1e293b', fontSize: '22px', fontWeight: 700 }}>
        <i className="fas fa-folder-open" style={{ marginRight: '10px', color: '#2e496b' }} />
        Panel dokumentów
      </h2>

      {/* Filters */}
      <div
        style={{
          background: '#fff',
          border: '1px solid #e2e8f0',
          borderRadius: '10px',
          padding: '16px 20px',
          marginBottom: '20px',
          display: 'flex',
          gap: '12px',
          flexWrap: 'wrap',
          alignItems: 'flex-end',
        }}
      >
        <div>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>
            Zawodnik
          </label>
          <input
            type="text"
            value={filterPlayer}
            onChange={e => setFilterPlayer(e.target.value)}
            placeholder="Szukaj zawodnika..."
            style={{ padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px', width: '180px' }}
          />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>
            Wgrał
          </label>
          <input
            type="text"
            value={filterUploadedBy}
            onChange={e => setFilterUploadedBy(e.target.value)}
            placeholder="E-mail wgrywającego..."
            style={{ padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px', width: '200px' }}
          />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>
            Data od
          </label>
          <input
            type="date"
            value={filterFrom}
            onChange={e => setFilterFrom(e.target.value)}
            style={{ padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px' }}
          />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>
            Data do
          </label>
          <input
            type="date"
            value={filterTo}
            onChange={e => setFilterTo(e.target.value)}
            style={{ padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px' }}
          />
        </div>
        {(filterPlayer || filterUploadedBy || filterFrom || filterTo) && (
          <button
            type="button"
            onClick={() => { setFilterPlayer(''); setFilterUploadedBy(''); setFilterFrom(''); setFilterTo(''); }}
            style={{ padding: '7px 14px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '6px', fontSize: '13px', cursor: 'pointer', fontWeight: 600 }}
          >
            ✕ Wyczyść filtry
          </button>
        )}
      </div>

      {/* Summary */}
      <div style={{ marginBottom: '12px', fontSize: '14px', color: '#64748b' }}>
        {loading ? 'Ładowanie...' : `Znaleziono ${filtered.length} dokumentów`}
      </div>

      {error && (
        <div style={{ color: '#ef4444', marginBottom: '16px', fontSize: '14px' }}>{error}</div>
      )}

      {/* Table */}
      <div
        style={{
          background: '#fff',
          border: '1px solid #e2e8f0',
          borderRadius: '10px',
          overflow: 'hidden',
        }}
      >
        {loading ? (
          <div style={{ padding: '48px', textAlign: 'center', color: '#94a3b8' }}>
            <i className="fas fa-spinner fa-spin" style={{ fontSize: '24px', marginBottom: '12px', display: 'block' }} />
            Ładowanie dokumentów...
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: '48px', textAlign: 'center', color: '#94a3b8' }}>
            <i className="fas fa-folder-open" style={{ fontSize: '32px', marginBottom: '12px', display: 'block' }} />
            Brak dokumentów spełniających kryteria wyszukiwania.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                  {['Zawodnik', 'Zadanie', 'Plik', 'Rozmiar', 'Wgrał', 'Data', 'Pobierz', ''].map(h => (
                    <th
                      key={h}
                      style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((doc, idx) => (
                  <tr
                    key={`${doc.taskId}-${doc.attachmentId}`}
                    style={{ borderBottom: '1px solid #f1f5f9', background: idx % 2 === 0 ? '#fff' : '#fafafa' }}
                  >
                    <td style={{ padding: '10px 14px', fontWeight: 500, color: '#1e293b', whiteSpace: 'nowrap' }}>
                      {doc.playerName}
                    </td>
                    <td style={{ padding: '10px 14px', color: '#64748b', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {doc.taskLabel || '—'}
                    </td>
                    <td style={{ padding: '10px 14px', color: '#1e293b' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <i
                          className={`fas ${
                            doc.contentType?.includes('pdf') ? 'fa-file-pdf' :
                            doc.contentType?.includes('image') ? 'fa-file-image' :
                            doc.contentType?.includes('word') ? 'fa-file-word' :
                            'fa-file'
                          }`}
                          style={{
                            color: doc.contentType?.includes('pdf') ? '#ef4444' :
                              doc.contentType?.includes('image') ? '#3b82f6' :
                              doc.contentType?.includes('word') ? '#2563eb' :
                              '#94a3b8',
                          }}
                        />
                        <span style={{ maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {doc.originalName || 'plik'}
                        </span>
                      </div>
                    </td>
                    <td style={{ padding: '10px 14px', color: '#64748b', whiteSpace: 'nowrap' }}>
                      {formatBytes(doc.size)}
                    </td>
                    <td style={{ padding: '10px 14px', color: '#64748b', maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {doc.uploadedBy || '—'}
                    </td>
                    <td style={{ padding: '10px 14px', color: '#64748b', whiteSpace: 'nowrap' }}>
                      {formatDate(doc.uploadedAt)}
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <button
                        type="button"
                        onClick={() => handleDownload(doc)}
                        style={{
                          padding: '5px 12px',
                          background: '#e0f2fe',
                          color: '#0369a1',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '13px',
                          fontWeight: 600,
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <i className="fas fa-download" style={{ marginRight: '4px' }} />
                        Pobierz
                      </button>
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <button
                        type="button"
                        onClick={() => setDeleteTarget(doc)}
                        title="Usuń dokument"
                        style={{
                          padding: '5px 10px',
                          background: '#fef2f2',
                          color: '#dc2626',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '14px',
                          cursor: 'pointer',
                        }}
                      >
                        <i className="fas fa-trash-alt" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmModal
        show={!!deleteTarget}
        title="Usunąć dokument?"
        message={deleteTarget ? `Czy na pewno chcesz usunąć plik "${deleteTarget.originalName || 'attachment'}"? Tej operacji nie można cofnąć.` : ''}
        confirmLabel={deleting ? 'Usuwanie...' : 'Usuń'}
        onConfirm={confirmDelete}
        onCancel={() => { if (!deleting) setDeleteTarget(null); }}
        icon="fas fa-trash-alt"
        danger
      />
    </div>
  );
};

export default AdminDocuments;
